import React, { useEffect, useState } from "react";
import { api, formatApiError, inr } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Save, X, Image as ImageIcon, FolderOpen, FileText, Upload } from "lucide-react";
import { MediaPickerModal } from "./MediaLibrary";
import Tour360EditorModal from "./Tour360Editor";

export function ImageUploader({ value, onChange, label = "Image URL", testId = "img-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(fullUrl);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] block mb-1">{label}</label>
      <div className="flex gap-2 items-start">
        {value && (
          <div className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-50">
            <img src={value} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://... or upload below"
            data-testid={`${testId}-url`}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
          />
          <label className={`inline-flex items-center gap-2 px-3 py-1.5 border-2 border-dashed border-[#F7941D]/40 rounded-lg text-xs font-semibold text-[#F7941D] cursor-pointer hover:bg-[#FEF3E2]/40 ${uploading ? "opacity-50" : ""}`}>
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
            {uploading ? "Uploading..." : "Upload Image"}
            <input type="file" accept="image/*" hidden disabled={uploading} onChange={handle} data-testid={`${testId}-file`} />
          </label>
        </div>
      </div>
    </div>
  );
}

// Uploads a single PDF via /admin/guides/upload-pdf and reports the
// resulting { pdf_file_id, file_name, file_size } back to the caller — the
// PDF equivalent of ImageUploader above, but the "value" it carries is a
// small object (a guide has no meaningful use for the raw file URL the way
// an image field does; the download itself goes through /guides/{slug}
// /download, not this uploaded URL directly).
export function PdfUploader({ value, onChange, testId = "pdf-uploader" }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Only PDF files are allowed");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/guides/upload-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ pdf_file_id: data.id, file_name: data.file_name, file_size: data.size });
      toast.success("PDF uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  const sizeLabel = value?.file_size ? `${(value.file_size / (1024 * 1024)).toFixed(1)} MB` : "";
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] block mb-1">Guide PDF</label>
      <div className="flex items-center gap-3">
        {value?.pdf_file_id ? (
          <div className="flex items-center gap-2 flex-1 min-w-0 border border-[#1a3c5e]/15 rounded-lg px-3 py-2 bg-[#FEF3E2]/40">
            <FileText className="w-4 h-4 text-[#1a3c5e] flex-shrink-0" />
            <span className="text-sm text-[#1a3c5e] truncate">{value.file_name || "guide.pdf"}</span>
            {sizeLabel && <span className="text-xs text-gray-500 flex-shrink-0">{sizeLabel}</span>}
          </div>
        ) : (
          <span className="text-sm text-gray-400 flex-1">No PDF uploaded yet</span>
        )}
        <label className={`inline-flex items-center gap-2 px-3 py-2 border-2 border-dashed border-[#F7941D]/40 rounded-lg text-xs font-semibold text-[#F7941D] cursor-pointer hover:bg-[#FEF3E2]/40 flex-shrink-0 ${uploading ? "opacity-50" : ""}`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? "Uploading..." : value?.pdf_file_id ? "Replace" : "Upload PDF"}
          <input type="file" accept="application/pdf" hidden disabled={uploading} onChange={handle} data-testid={`${testId}-file`} />
        </label>
      </div>
    </div>
  );
}

// `captions`/`onCaptionsChange` are optional and additive — every existing
// caller (Portfolio galleries etc.) that doesn't pass them keeps behaving
// exactly as before. When passed (the Services package gallery does), a
// caption input appears under each image and stays index-aligned with
// `value` through add/remove/library-pick.
export function MultiImageEditor({ value = [], onChange, label, testId = "multi-img", pickerCategory = "images", captions, onCaptionsChange }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const hasCaptions = Array.isArray(captions) && typeof onCaptionsChange === "function";
  const captionAt = (i) => (hasCaptions ? (captions[i] || "") : "");
  const update = (i, v) => onChange(value.map((x, idx) => idx === i ? v : x));
  const updateCaption = (i, v) => {
    if (!hasCaptions) return;
    onCaptionsChange(value.map((_, idx) => idx === i ? v : captionAt(idx)));
  };
  const remove = (i) => {
    onChange(value.filter((_, idx) => idx !== i));
    if (hasCaptions) onCaptionsChange(captions.filter((_, idx) => idx !== i));
  };
  const add = () => {
    onChange([...value, ""]);
    if (hasCaptions) onCaptionsChange([...captions, ""]);
  };
  const pick = (url) => {
    onChange([...value, url]);
    if (hasCaptions) onCaptionsChange([...captions, ""]);
  };
  return (
    <div>
      {label && <label className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] block mb-1">{label}</label>}
      <div className="space-y-2">
        {value.map((v, i) => (
          <div key={i} className="flex gap-2 items-start">
            <span className="text-xs text-gray-500 w-6 mt-2.5">{i + 1}.</span>
            <div className="flex-1 space-y-1.5">
              <ImageUploader value={v} onChange={(nv) => update(i, nv)} label="" testId={`${testId}-${i}`} />
              {hasCaptions && (
                <input
                  value={captionAt(i)}
                  onChange={(e) => updateCaption(i, e.target.value)}
                  placeholder='Label shown on this image, e.g. "Floor Plan - Ground Floor"'
                  data-testid={`${testId}-${i}-caption`}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
                />
              )}
            </div>
            <button type="button" onClick={() => remove(i)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg mt-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <div className="flex gap-3 items-center">
          <button type="button" onClick={add} className="text-xs font-semibold text-[#F7941D] hover:underline" data-testid={`${testId}-add`}>+ Add image</button>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="text-xs font-semibold text-[#1a3c5e] hover:underline flex items-center gap-1"
            data-testid={`${testId}-pick`}
          >
            <FolderOpen className="w-3 h-3" /> Pick from Library
          </button>
        </div>
      </div>
      <MediaPickerModal
        open={pickerOpen}
        category={pickerCategory}
        onClose={() => setPickerOpen(false)}
        onPick={pick}
      />
    </div>
  );
}

export function ListEditor({ value = [], onChange, label, placeholder, testId = "list-editor", showLibraryPicker = false, pickerCategory = "videos" }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const update = (i, v) => onChange(value.map((x, idx) => idx === i ? v : x));
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([...value, ""]);
  return (
    <div>
      {label && <label className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] block mb-1">{label}</label>}
      <div className="space-y-1.5">
        {value.map((v, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input
              value={v}
              onChange={(e) => update(i, e.target.value)}
              placeholder={placeholder}
              data-testid={`${testId}-${i}`}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]"
            />
            <button type="button" onClick={() => remove(i)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <div className="flex gap-3 items-center">
          <button type="button" onClick={add} className="text-xs font-semibold text-[#F7941D] hover:underline" data-testid={`${testId}-add`}>+ Add item</button>
          {showLibraryPicker && (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="text-xs font-semibold text-[#1a3c5e] hover:underline flex items-center gap-1"
              data-testid={`${testId}-pick`}
            >
              <FolderOpen className="w-3 h-3" /> Pick from Library
            </button>
          )}
        </div>
      </div>
      {showLibraryPicker && (
        <MediaPickerModal
          open={pickerOpen}
          category={pickerCategory}
          onClose={() => setPickerOpen(false)}
          onPick={(url) => onChange([...value, url])}
        />
      )}
    </div>
  );
}

// ===== SERVICES EDITOR (Package Management) =====
const BADGE_OPTIONS = [
  { value: "", label: "— No Badge —", color: "" },
  { value: "popular", label: "Most Popular", color: "#F7941D" },
  { value: "recommended", label: "Recommended", color: "#10b981" },
  { value: "best-value", label: "Best Value", color: "#3b82f6" },
  { value: "new-launch", label: "New Launch", color: "#8b5cf6" },
  { value: "limited-offer", label: "Limited Offer", color: "#D32F2F" },
  { value: "premium-choice", label: "Premium Choice", color: "#1a3c5e" },
];
const BADGE_LABEL = Object.fromEntries(BADGE_OPTIONS.map(b => [b.value, b.label]));
const BADGE_DEFAULT_COLOR = Object.fromEntries(BADGE_OPTIONS.map(b => [b.value, b.color]));

const EMPTY_SERVICE = {
  slug: "", name: "", subtitle: "", price: 0, original_price: 0, emi_per_month: 0,
  delivery_days: 10, delivery_text: "", floor_tag: "", revisions: "2", tagline: "",
  icon: "blueprint", includes_list: [],
  is_active: true, is_popular: false,
  sort_order: 999, badge: "", badge_color: "#F7941D",
  media_images: [], media_360: [], media_videos: [],
  media_captions: [], sample_label: "Sample Drawing",
};

export function ServicesEditor() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/services");
      setServices(r.data);
    } catch {
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/services");
        if (!cancelled) setServices(r.data);
      } catch {
        if (!cancelled) toast.error("Failed to load packages");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    try {
      if (isNew) {
        await api.post("/admin/services", editing);
        toast.success(`${editing.name} created`);
      } else {
        await api.put(`/admin/services/${editing.slug}`, editing);
        toast.success(`${editing.name} updated`);
      }
      setEditing(null);
      setIsNew(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (svc) => {
    if (!window.confirm(`Delete package "${svc.name}"? Inactive orders are kept.`)) return;
    try {
      await api.delete(`/admin/services/${svc.slug}`);
      toast.success(`${svc.name} deleted`);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (typeof detail === "string" && detail.includes("active order")) {
        if (window.confirm(detail + "\n\nForce delete anyway?")) {
          try {
            await api.delete(`/admin/services/${svc.slug}?force=true`);
            toast.success("Force deleted");
            load();
          } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail));
          }
        }
      } else {
        toast.error(formatApiError(detail));
      }
    }
  };

  const toggleActive = async (svc) => {
    try {
      await api.put(`/admin/services/${svc.slug}`, { is_active: !svc.is_active });
      toast.success(`${svc.name} ${svc.is_active ? "disabled" : "enabled"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const moveUp = async (idx) => {
    if (idx === 0) return;
    const order = services.map(s => s.slug);
    [order[idx - 1], order[idx]] = [order[idx], order[idx - 1]];
    try {
      const r = await api.post("/admin/services/reorder", { order });
      setServices(r.data);
    } catch { toast.error("Reorder failed"); }
  };
  const moveDown = async (idx) => {
    if (idx === services.length - 1) return;
    const order = services.map(s => s.slug);
    [order[idx], order[idx + 1]] = [order[idx + 1], order[idx]];
    try {
      const r = await api.post("/admin/services/reorder", { order });
      setServices(r.data);
    } catch { toast.error("Reorder failed"); }
  };

  const onBadgeChange = (val) => {
    setEditing(prev => ({
      ...prev,
      badge: val,
      // Auto-pick the default colour for that badge if user hasn't set one explicitly recently
      badge_color: BADGE_DEFAULT_COLOR[val] || prev.badge_color || "#F7941D",
      // Keep legacy is_popular in sync for backward-compat with public pages
      is_popular: val === "popular",
    }));
  };

  return (
    <div className="space-y-4" data-testid="services-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Packages</h2>
          <p className="text-xs text-gray-500 mt-0.5">Drag to reorder · Toggle visibility · Manage media & badges</p>
        </div>
        <button
          onClick={() => { setEditing({ ...EMPTY_SERVICE }); setIsNew(true); }}
          data-testid="new-package-btn"
          className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white font-bold rounded-xl text-sm shadow-sm"
        >+ Add Package</button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-gray-500 text-sm">Loading packages...</div>
      ) : (
        <div className="space-y-3">
          {services.map((s, idx) => {
            const badgeOpt = BADGE_OPTIONS.find(b => b.value === s.badge);
            return (
              <div
                key={s.slug}
                className={`bg-white rounded-2xl border ${s.is_active ? "border-[#1a3c5e]/10" : "border-gray-300 opacity-60"} p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4`}
                data-testid={`package-row-${s.slug}`}
              >
                {/* Reorder controls */}
                <div className="flex sm:flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={() => moveUp(idx)}
                    disabled={idx === 0}
                    title="Move up"
                    data-testid={`up-${s.slug}`}
                    className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed text-[#1a3c5e] font-bold"
                  >↑</button>
                  <button
                    onClick={() => moveDown(idx)}
                    disabled={idx === services.length - 1}
                    title="Move down"
                    data-testid={`down-${s.slug}`}
                    className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed text-[#1a3c5e] font-bold"
                  >↓</button>
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#FEF3E2] text-[#F7941D]">{s.slug}</span>
                    {badgeOpt && badgeOpt.value && (
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded text-white" style={{ backgroundColor: s.badge_color || badgeOpt.color }}>{badgeOpt.label}</span>
                    )}
                    {!s.is_active && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-gray-400 text-white">Hidden</span>}
                  </div>
                  <h3 className="font-display font-bold text-[#1a3c5e] truncate">{s.name}</h3>
                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                    {inr(s.price)} · {s.delivery_text || `${s.delivery_days}d`} · {s.includes_list?.length || 0} features
                    {(s.media_360?.length || 0) > 0 && <> · {s.media_360.length} × 360°</>}
                    {(s.media_videos?.length || 0) > 0 && <> · {s.media_videos.length} video</>}
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleActive(s)}
                    data-testid={`toggle-${s.slug}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${s.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-gray-200 text-gray-600 hover:bg-gray-300"}`}
                  >{s.is_active ? "Enabled" : "Disabled"}</button>
                  <button
                    onClick={() => { setEditing({ ...EMPTY_SERVICE, ...s }); setIsNew(false); }}
                    data-testid={`edit-service-${s.slug}`}
                    className="px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#122a42] text-white text-xs font-semibold rounded-lg"
                  >Edit</button>
                  <button
                    onClick={() => remove(s)}
                    data-testid={`delete-${s.slug}`}
                    className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-semibold rounded-lg"
                  >Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <Modal title={isNew ? "New Package" : `Edit ${editing.name}`} onClose={() => { setEditing(null); setIsNew(false); }}>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field
                label="Slug (URL identifier)"
                value={editing.slug}
                onChange={(v) => setEditing({ ...editing, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
                placeholder="e.g. budget-plus"
                disabled={!isNew}
              />
              <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
            </div>
            <Field label="Subtitle" value={editing.subtitle} onChange={(v) => setEditing({ ...editing, subtitle: v })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price (₹)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: parseFloat(v) || 0 })} />
              <Field label="Original Price (₹)" type="number" value={editing.original_price} onChange={(v) => setEditing({ ...editing, original_price: parseFloat(v) || 0 })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="EMI/month" type="number" value={editing.emi_per_month} onChange={(v) => setEditing({ ...editing, emi_per_month: parseFloat(v) || 0 })} />
              <Field label="Delivery days" type="number" value={editing.delivery_days} onChange={(v) => setEditing({ ...editing, delivery_days: parseInt(v) || 0 })} />
              <Field label="Delivery text" value={editing.delivery_text} onChange={(v) => setEditing({ ...editing, delivery_text: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Floor tag" value={editing.floor_tag} onChange={(v) => setEditing({ ...editing, floor_tag: v })} />
              <Field label="Revisions" value={editing.revisions} onChange={(v) => setEditing({ ...editing, revisions: v })} />
            </div>
            <Field label="Tagline" value={editing.tagline} onChange={(v) => setEditing({ ...editing, tagline: v })} />
            <ListEditor label="Includes (features list)" value={editing.includes_list || []} onChange={(v) => setEditing({ ...editing, includes_list: v })} placeholder="e.g. Electrical layout" testId="svc-feat" />

            {/* Badge */}
            <div className="bg-[#FEF3E2]/30 rounded-xl p-4 space-y-3 border border-[#F7941D]/20">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e]">Badge</div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
                <Select
                  label="Choose preset"
                  value={editing.badge || ""}
                  onChange={onBadgeChange}
                  options={BADGE_OPTIONS.map(b => [b.value, b.label])}
                />
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] block mb-1">Color</label>
                  <input
                    type="color"
                    value={editing.badge_color || "#F7941D"}
                    onChange={(e) => setEditing({ ...editing, badge_color: e.target.value })}
                    data-testid="badge-color-input"
                    className="w-16 h-10 rounded-lg border border-gray-300 cursor-pointer"
                  />
                </div>
              </div>
              {editing.badge && (
                <div className="text-xs text-gray-600">
                  Preview:&nbsp;
                  <span className="inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded text-white" style={{ backgroundColor: editing.badge_color || "#F7941D" }}>
                    {BADGE_LABEL[editing.badge] || editing.badge}
                  </span>
                </div>
              )}
            </div>

            {/* Media — 360° */}
            <div className="bg-blue-50/40 rounded-xl p-4 border border-blue-200 space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e]">360° Panorama Images</div>
              <div className="text-[11px] text-gray-600 -mt-1">Equirectangular JPGs (2:1 ratio). Upload via Image Uploader or paste URLs.</div>
              <MultiImageEditor
                value={editing.media_360 || []}
                onChange={(v) => setEditing({ ...editing, media_360: v })}
                label=""
                testId="pkg-360"
                pickerCategory="pano360"
              />
            </div>

            {/* Media — Videos */}
            <div className="bg-red-50/40 rounded-xl p-4 border border-red-200 space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e]">YouTube Videos</div>
              <div className="text-[11px] text-gray-600 -mt-1">Paste full YouTube URLs (e.g. https://youtu.be/abc123).</div>
              <ListEditor
                value={editing.media_videos || []}
                onChange={(v) => setEditing({ ...editing, media_videos: v })}
                label=""
                placeholder="https://www.youtube.com/watch?v=..."
                testId="pkg-video"
                showLibraryPicker={true}
                pickerCategory="videos"
              />
            </div>

            {/* Media — Extra gallery images */}
            <div className="bg-emerald-50/40 rounded-xl p-4 border border-emerald-200 space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e]">Gallery Images (override default)</div>
              <div className="text-[11px] text-gray-600 -mt-1">Leave empty to use the default package images on the public page. Only use real photos of what this package actually delivers — e.g. real sample floor plans for a 2D-only package — the label below each image and the watermark both promise the client something specific.</div>
              <label className="text-xs font-semibold text-[#1a3c5e] block pt-1">Watermark text (stamped across every image on this package's page)</label>
              <input
                value={editing.sample_label ?? "Sample Drawing"}
                onChange={(e) => setEditing({ ...editing, sample_label: e.target.value })}
                placeholder="Sample Drawing"
                data-testid="svc-sample-label"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <MultiImageEditor
                value={editing.media_images || []}
                onChange={(v) => setEditing({ ...editing, media_images: v })}
                captions={editing.media_captions || []}
                onCaptionsChange={(v) => setEditing({ ...editing, media_captions: v })}
                label=""
                testId="pkg-img"
                pickerCategory="package-media"
              />
            </div>

            <div className="flex gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!editing.is_active}
                  onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                  data-testid="svc-active-cb"
                  className="w-4 h-4 accent-[#F7941D]"
                />
                <span className="font-semibold text-[#1a3c5e]">Active (visible on site)</span>
              </label>
            </div>

            <SaveBar onSave={save} onCancel={() => { setEditing(null); setIsNew(false); }} testId="save-service" />
          </div>
        </Modal>
      )}
    </div>
  );
}

// ===== PORTFOLIO EDITOR =====
const EMPTY_PORTFOLIO = {
  client: "", city: "", state: "", category: "house", style: "modern", floors: "2",
  achievement: "", service: "", package: "premium", plot_size: "", built_area: "",
  year: "2025", designer: "Rohit Designer", rating: 5, summary: "",
  key_features: [], image: "", thumbs: [], gallery: [], media_360: [], tour_360: null,
};

export function PortfolioEditor() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [showTourEditor, setShowTourEditor] = useState(false);
  const load = () => api.get("/portfolio").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (isNew) {
        await api.post("/admin/portfolio", editing);
        toast.success("Portfolio added");
      } else {
        await api.put(`/admin/portfolio/${editing.id}`, editing);
        toast.success("Portfolio updated");
      }
      setEditing(null);
      setIsNew(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this portfolio item?")) return;
    try { await api.delete(`/admin/portfolio/${pid}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Portfolio ({items.length})</h2>
        <button onClick={() => { setEditing({ ...EMPTY_PORTFOLIO }); setIsNew(true); }} data-testid="new-portfolio-btn" className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white font-semibold rounded-xl text-sm">+ Add Project</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(p => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#1a3c5e]/10 overflow-hidden">
            <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
              {p.image && <img src={p.image} alt={p.client} className="w-full h-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="font-semibold text-[#1a3c5e] text-sm truncate">{p.client}</div>
              <div className="text-xs text-gray-500 truncate">
                {p.service} · {p.city}, {p.state}
                {(p.media_360?.length || 0) > 0 && <> · {p.media_360.length} × 360°</>}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => { setEditing({ ...p, thumbs: p.thumbs || [], gallery: p.gallery || [], key_features: p.key_features || [], media_360: p.media_360 || [], tour_360: p.tour_360 || null }); setIsNew(false); }} data-testid={`edit-portfolio-${p.id}`} className="flex-1 px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#122a42] text-white text-xs font-semibold rounded-lg">Edit</button>
                <button onClick={() => remove(p.id)} data-testid={`delete-portfolio-${p.id}`} className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={isNew ? "Add Project" : `Edit ${editing.client}`} onClose={() => { setEditing(null); setIsNew(false); }}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Client name" value={editing.client} onChange={(v) => setEditing({ ...editing, client: v })} />
              <Field label="Designer" value={editing.designer} onChange={(v) => setEditing({ ...editing, designer: v })} />
              <Field label="City" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} />
              <Field label="State" value={editing.state} onChange={(v) => setEditing({ ...editing, state: v })} />
            </div>
            <Field label="Service title (e.g. 'Modern Luxury Villa')" value={editing.service} onChange={(v) => setEditing({ ...editing, service: v })} />
            <div className="grid grid-cols-3 gap-3">
              <Select label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={[["house","House"],["villa","Villa"],["other","Other Building"],["construction","Construction"]]} />
              <Field label="Style" value={editing.style} onChange={(v) => setEditing({ ...editing, style: v })} placeholder="modern, classic, rajputana..." />
              <Field label="Floors (1-4)" value={editing.floors} onChange={(v) => setEditing({ ...editing, floors: v })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Plot size" value={editing.plot_size} onChange={(v) => setEditing({ ...editing, plot_size: v })} placeholder="30 x 60 ft" />
              <Field label="Built area" value={editing.built_area} onChange={(v) => setEditing({ ...editing, built_area: v })} placeholder="3200 sq.ft" />
              <Field label="Year" value={editing.year} onChange={(v) => setEditing({ ...editing, year: v })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Select label="Package" value={editing.package} onChange={(v) => setEditing({ ...editing, package: v })} options={[["basic","Basic"],["premium","Premium"],["luxury","Luxury"]]} />
              <Select label="Achievement" value={editing.achievement || ""} onChange={(v) => setEditing({ ...editing, achievement: v })} options={[["","None"],["best","Best Design Award"]]} />
              <Field label="Rating (1-5)" type="number" value={editing.rating} onChange={(v) => setEditing({ ...editing, rating: parseInt(v) || 5 })} />
            </div>
            <TextArea label="Project Summary" value={editing.summary} onChange={(v) => setEditing({ ...editing, summary: v })} rows={4} />
            <ListEditor label="Key Features" value={editing.key_features} onChange={(v) => setEditing({ ...editing, key_features: v })} placeholder="e.g. Premium photorealistic 3D" testId="pf-feat" />

            <ImageUploader label="Main Image (Hero)" value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} testId="pf-main" />
            <MultiImageEditor label="Thumbnail Images (shown in card)" value={editing.thumbs} onChange={(v) => setEditing({ ...editing, thumbs: v })} testId="pf-thumb" />
            <MultiImageEditor label="Gallery Images (shown in detail page)" value={editing.gallery} onChange={(v) => setEditing({ ...editing, gallery: v })} testId="pf-gal" />

            <div className="bg-blue-50/40 rounded-xl p-4 border border-blue-200 space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e]">360° Panorama Images</div>
              <div className="text-[11px] text-gray-600 -mt-1">Equirectangular JPGs (2:1 ratio). Upload via Image Uploader or paste URLs.</div>
              <MultiImageEditor
                value={editing.media_360 || []}
                onChange={(v) => setEditing({ ...editing, media_360: v })}
                label=""
                testId="pf-360"
                pickerCategory="pano360"
              />
            </div>

            <div className="bg-emerald-50/40 rounded-xl p-4 border border-emerald-200 space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e]">Interactive 360° Tour (rooms linked by hotspots)</div>
              <div className="text-[11px] text-gray-600 -mt-1">
                {editing.tour_360?.scenes?.length
                  ? `${editing.tour_360.scenes.length} scene(s) configured${editing.tour_360.audio_url ? " · narration attached" : ""}`
                  : "Not set up yet — visitors walk between rooms by clicking arrows."}
              </div>
              <button type="button" onClick={() => setShowTourEditor(true)} data-testid="pf-tour-edit-btn" className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg">
                {editing.tour_360?.scenes?.length ? "Edit 360° Tour →" : "Build 360° Tour →"}
              </button>
            </div>

            <SaveBar onSave={save} onCancel={() => { setEditing(null); setIsNew(false); }} testId="save-portfolio" />
          </div>
        </Modal>
      )}

      {showTourEditor && editing && (
        <Tour360EditorModal
          value={editing.tour_360}
          onSave={(tour) => setEditing({ ...editing, tour_360: tour })}
          onClose={() => setShowTourEditor(false)}
        />
      )}
    </div>
  );
}

// ===== REVIEWS EDITOR =====
export function ReviewsEditor() {
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/reviews").then(r => setReviews(r.data));
  useEffect(() => { load(); }, []);

  const togglePublish = async (r) => {
    try { await api.put(`/admin/reviews/${r.id}`, { is_published: !r.is_published }); toast.success(r.is_published ? "Hidden" : "Published"); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try { await api.delete(`/admin/reviews/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const save = async () => {
    try { await api.put(`/admin/reviews/${editing.id}`, editing); toast.success("Saved"); setEditing(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Reviews ({reviews.length})</h2>
      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 overflow-hidden">
        {reviews.length === 0 ? <p className="text-center text-gray-500 py-10">No reviews yet.</p> : (
          <div className="divide-y divide-gray-100">
            {reviews.map(r => (
              <div key={r.id} className="p-4 hover:bg-[#FEF3E2]/30 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-[#1a3c5e] text-sm">{r.reviewer_name}</span>
                    <span className="text-xs text-gray-500">· {r.city}</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#FEF3E2] text-[#F7941D] text-[10px] font-bold uppercase">{r.service_slug}</span>
                    <span className="text-xs text-[#F7941D]">{"★".repeat(r.rating)}</span>
                    {!r.is_published && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-bold uppercase">Hidden</span>}
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-2">{r.review_text}</p>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button onClick={() => setEditing({ ...r })} data-testid={`edit-review-${r.id}`} className="px-2.5 py-1 bg-[#1a3c5e] hover:bg-[#122a42] text-white text-xs font-semibold rounded">Edit</button>
                  <button onClick={() => togglePublish(r)} data-testid={`toggle-review-${r.id}`} className={`px-2.5 py-1 text-xs font-semibold rounded ${r.is_published ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{r.is_published ? "Hide" : "Show"}</button>
                  <button onClick={() => remove(r.id)} className="px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded">Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <Modal title="Edit Review" onClose={() => setEditing(null)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Reviewer name" value={editing.reviewer_name} onChange={(v) => setEditing({ ...editing, reviewer_name: v })} />
              <Field label="City" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select label="Package" value={editing.service_slug} onChange={(v) => setEditing({ ...editing, service_slug: v })} options={[["basic","Basic"],["premium","Premium"],["luxury","Luxury"]]} />
              <Field label="Rating (1-5)" type="number" value={editing.rating} onChange={(v) => setEditing({ ...editing, rating: parseInt(v) || 5 })} />
            </div>
            <TextArea label="Review Text" value={editing.review_text} onChange={(v) => setEditing({ ...editing, review_text: v })} rows={5} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!editing.is_published} onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })} className="w-4 h-4 accent-[#F7941D]" />
              <span className="font-semibold text-[#1a3c5e]">Published (visible on site)</span>
            </label>
            <SaveBar onSave={save} onCancel={() => setEditing(null)} testId="save-review" />
          </div>
        </Modal>
      )}
    </div>
  );
}

// ===== SETTINGS EDITOR =====
export function SettingsEditor() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then(r => setSettings(r.data)); }, []);
  if (!settings) return <div className="bg-white rounded-2xl p-10 text-center text-gray-500">Loading…</div>;

  const save = async () => {
    try { await api.put("/admin/settings", settings); toast.success("Settings saved · refresh site to see changes"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Site Settings</h2>
      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6 space-y-4">
        <h3 className="font-display font-bold text-[#1a3c5e]">Hero Section</h3>
        <Field label="Headline" value={settings.hero_headline} onChange={(v) => setSettings({ ...settings, hero_headline: v })} />
        <Field label="Subtext" value={settings.hero_subtext} onChange={(v) => setSettings({ ...settings, hero_subtext: v })} />
        <Field label="Price Badge text" value={settings.hero_price_badge} onChange={(v) => setSettings({ ...settings, hero_price_badge: v })} />
        <ImageUploader label="Hero Banner Image" value={settings.hero_banner_image} onChange={(v) => setSettings({ ...settings, hero_banner_image: v })} testId="hero-banner" />
        <Field label="Tagline (under logo)" value={settings.tagline} onChange={(v) => setSettings({ ...settings, tagline: v })} />
      </div>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6 space-y-4">
        <h3 className="font-display font-bold text-[#1a3c5e]">Branding</h3>
        <ImageUploader label="Logo Image" value={settings.logo_image} onChange={(v) => setSettings({ ...settings, logo_image: v })} testId="logo-img" />
      </div>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6 space-y-4">
        <h3 className="font-display font-bold text-[#1a3c5e]">Contact</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Phone (display)" value={settings.phone} onChange={(v) => setSettings({ ...settings, phone: v })} />
          <Field label="WhatsApp Number (digits only)" value={settings.whatsapp_number} onChange={(v) => setSettings({ ...settings, whatsapp_number: v })} placeholder="919050785528" />
        </div>
        <Field label="Email" value={settings.email} onChange={(v) => setSettings({ ...settings, email: v })} />
      </div>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6 space-y-4">
        <h3 className="font-display font-bold text-[#1a3c5e]">Misc</h3>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!settings.countdown_active} onChange={(e) => setSettings({ ...settings, countdown_active: e.target.checked })} className="w-4 h-4 accent-[#F7941D]" />
          <span className="font-semibold text-[#1a3c5e]">Show urgency countdown bar at top</span>
        </label>
      </div>

      <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6 space-y-3">
        <h3 className="font-display font-bold text-[#1a3c5e]">Outbound Webhook (n8n / Zapier / Make.com)</h3>
        <p className="text-xs text-gray-500">When set, every in-app notification (order assigned, delivered, completed, revision requested...) will also be POSTed to this URL. Useful for sending WhatsApp / SMS / Email via your own automation.</p>
        <Field
          label="Webhook URL"
          value={settings.n8n_webhook_url}
          onChange={(v) => setSettings({ ...settings, n8n_webhook_url: v })}
          placeholder="https://your-n8n.example.com/webhook/housedesignwala"
        />
        <p className="text-[10px] text-gray-400">Payload: <span className="font-mono">{`{user_id, type, title, message, link, order_id, created_at}`}</span></p>
      </div>

      <div className="sticky bottom-4 bg-white rounded-2xl border-2 border-[#F7941D] shadow-xl p-4 flex justify-end">
        <button onClick={save} data-testid="save-settings" className="px-6 py-2.5 bg-[#F7941D] hover:bg-[#e0861a] text-white font-bold rounded-xl flex items-center gap-2 shadow-md">
          <Save className="w-4 h-4" /> Save All Settings
        </button>
      </div>
    </div>
  );
}

// ===== COUPONS EDITOR =====
const EMPTY_COUPON = { code: "", discount_percent: 10, min_order: 0, uses_left: 100, expiry_days: 365 };

export function CouponsEditor() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const load = () => api.get("/admin/coupons").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (isNew) await api.post("/admin/coupons", editing);
      else await api.put(`/admin/coupons/${editing.code}`, editing);
      toast.success("Saved");
      setEditing(null); setIsNew(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try { await api.delete(`/admin/coupons/${code}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Coupons</h2>
        <button onClick={() => { setEditing({ ...EMPTY_COUPON }); setIsNew(true); }} data-testid="new-coupon-btn" className="px-4 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white font-semibold rounded-xl text-sm">+ Add Coupon</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(c => (
          <div key={c.id} className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-5">
            <div className="font-mono font-extrabold text-xl text-[#F7941D]">{c.code}</div>
            <div className="text-xs text-gray-600 mt-1">{c.discount_percent}% off · Min ₹{c.min_order} · {c.uses_left} uses left</div>
            {c.expiry && (
              <div className={`text-xs mt-1 ${new Date(c.expiry) < new Date() ? "text-red-600 font-semibold" : "text-gray-500"}`}>
                {new Date(c.expiry) < new Date() ? "Expired " : "Expires "}
                {new Date(c.expiry).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              </div>
            )}
            <div className="flex gap-2 mt-3">
              <button onClick={() => { setEditing({ ...c }); setIsNew(false); }} data-testid={`edit-coupon-${c.code}`} className="flex-1 px-3 py-1.5 bg-[#1a3c5e] text-white text-xs font-semibold rounded-lg">Edit</button>
              <button onClick={() => remove(c.code)} className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-semibold rounded-lg">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={isNew ? "New Coupon" : `Edit ${editing.code}`} onClose={() => { setEditing(null); setIsNew(false); }}>
          <div className="space-y-3">
            <Field label="Code" value={editing.code} onChange={(v) => setEditing({ ...editing, code: v.toUpperCase() })} disabled={!isNew} />
            <div className="grid grid-cols-3 gap-3">
              <Field label="Discount %" type="number" value={editing.discount_percent} onChange={(v) => setEditing({ ...editing, discount_percent: parseFloat(v) || 0 })} />
              <Field label="Min Order ₹" type="number" value={editing.min_order} onChange={(v) => setEditing({ ...editing, min_order: parseFloat(v) || 0 })} />
              <Field label="Uses left" type="number" value={editing.uses_left} onChange={(v) => setEditing({ ...editing, uses_left: parseInt(v) || 0 })} />
            </div>
            {isNew && (
              <Field label="Valid for (days)" type="number" value={editing.expiry_days} onChange={(v) => setEditing({ ...editing, expiry_days: parseInt(v) || 365 })} />
            )}
            <SaveBar onSave={save} onCancel={() => { setEditing(null); setIsNew(false); }} testId="save-coupon" />
          </div>
        </Modal>
      )}
    </div>
  );
}

// ===== Helpers =====
function Field({ label, value, onChange, type = "text", placeholder, disabled }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] block mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D] disabled:bg-gray-100"
      />
    </div>
  );
}
function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] block mb-1">{label}</label>
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
    </div>
  );
}
function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] block mb-1">{label}</label>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#F7941D]">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="font-display text-lg font-bold text-[#1a3c5e]">{title}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
function SaveBar({ onSave, onCancel, testId }) {
  return (
    <div className="pt-4 border-t border-gray-200 flex justify-end gap-2">
      <button onClick={onCancel} className="px-5 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50">Cancel</button>
      <button onClick={onSave} data-testid={testId} className="px-5 py-2 bg-[#F7941D] hover:bg-[#e0861a] text-white font-bold rounded-lg flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
    </div>
  );
}
